'use client';

import React, { useState } from 'react';
import { Facebook as FacebookIcon, Instagram as InstagramIcon, Calendar, Clock, Target, Users, MapPin, Tag, Send, AlertCircle, CheckCircle2, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import Modal from '@/components/ui/Modal';

interface MetaCredentials {
  appId: string;
  appSecret: string;
  accessToken: string;
  pageId: string;
  igAccountId: string;
}

interface AudienceTargeting {
  ageMin: number;
  ageMax: number;
  locations: string;
  interests: string;
}

interface PublishResult {
  platform: string;
  postId?: string;
  error?: string;
}

interface PublishModalProps {
  open: boolean;
  onClose: () => void;
  flyerImageBase64: string | null;
}

type ScheduleMode = 'now' | 'scheduled';

const INTEREST_SUGGESTIONS = [
  'Marketing', 'Small Business', 'Entrepreneurship', 'E-commerce',
  'Fashion', 'Food & Dining', 'Fitness', 'Technology', 'Travel', 'Real Estate',
];

export default function PublishModal({ open, onClose, flyerImageBase64 }: PublishModalProps) {
  // Platform selection
  const [platforms, setPlatforms] = useState<{ facebook: boolean; instagram: boolean }>({
    facebook: true,
    instagram: false,
  });

  // Meta credentials
  const [creds, setCreds] = useState<MetaCredentials>({
    appId: '',
    appSecret: '',
    accessToken: '',
    pageId: '',
    igAccountId: '',
  });
  const [showCreds, setShowCreds] = useState(true);

  // Post content
  const [caption, setCaption] = useState('');

  // Audience targeting
  const [audience, setAudience] = useState<AudienceTargeting>({
    ageMin: 18,
    ageMax: 65,
    locations: '',
    interests: '',
  });
  const [showAudience, setShowAudience] = useState(false);
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);

  // Scheduling
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('now');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');

  // Publish state
  const [isPublishing, setIsPublishing] = useState(false);
  const [results, setResults] = useState<PublishResult[] | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const togglePlatform = (p: 'facebook' | 'instagram') => {
    setPlatforms((prev) => ({ ...prev, [p]: !prev[p] }));
  };

  const toggleInterest = (interest: string) => {
    setSelectedInterests((prev) =>
      prev.includes(interest) ? prev.filter((i) => i !== interest) : [...prev, interest]
    );
  };

  const getScheduledTime = (): string | undefined => {
    if (scheduleMode !== 'scheduled' || !scheduleDate || !scheduleTime) return undefined;
    return new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
  };

  const canPublish = () => {
    if (!flyerImageBase64) return false;
    if (!platforms.facebook && !platforms.instagram) return false;
    if (!creds.accessToken.trim() || !creds.pageId.trim()) return false;
    if (platforms.instagram && !creds.igAccountId.trim()) return false;
    if (scheduleMode === 'scheduled' && (!scheduleDate || !scheduleTime)) return false;
    return true;
  };

  const handlePublish = async () => {
    if (!canPublish() || isPublishing) return;
    setIsPublishing(true);
    setGlobalError(null);
    setResults(null);

    const selectedPlatforms: ('facebook' | 'instagram')[] = [];
    if (platforms.facebook) selectedPlatforms.push('facebook');
    if (platforms.instagram) selectedPlatforms.push('instagram');

    const allInterests = [
      ...selectedInterests,
      ...audience.interests.split(',').map((s) => s.trim()).filter(Boolean),
    ];

    try {
      const res = await fetch('/api/meta/publish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId: creds.appId,
          appSecret: creds.appSecret,
          accessToken: creds.accessToken,
          pageId: creds.pageId,
          igAccountId: creds.igAccountId || undefined,
          platforms: selectedPlatforms,
          imageBase64: flyerImageBase64,
          caption,
          targeting: {
            ageMin: audience.ageMin,
            ageMax: audience.ageMax,
            locations: audience.locations.split(',').map((s) => s.trim()).filter(Boolean),
            interests: allInterests,
          },
          scheduledTime: getScheduledTime(),
        }),
      });

      const data = await res.json();
      if (data.error) {
        setGlobalError(data.error);
      } else {
        setResults(data.results);
      }
    } catch {
      setGlobalError('Network error. Please check your connection and try again.');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleClose = () => {
    if (isPublishing) return;
    setResults(null);
    setGlobalError(null);
    onClose();
  };

  const minScheduleDate = new Date();
  minScheduleDate.setMinutes(minScheduleDate.getMinutes() + 10);
  const minDateStr = minScheduleDate.toISOString().slice(0, 10);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Publish Flyer"
      subtitle="Post your flyer to Facebook and Instagram via Meta Graph API"
      size="lg"
    >
      <div className="p-6 space-y-6">
        {/* No flyer warning */}
        {!flyerImageBase64 && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
            <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">No flyer image found. Please generate or create a flyer first before publishing.</p>
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="space-y-2">
            {results.map((r) => (
              <div
                key={r.platform}
                className={`flex items-start gap-3 p-4 rounded-xl border ${
                  r.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
                }`}
              >
                {r.error ? (
                  <AlertCircle size={16} className="text-red-600 shrink-0 mt-0.5" />
                ) : (
                  <CheckCircle2 size={16} className="text-green-600 shrink-0 mt-0.5" />
                )}
                <div>
                  <p className={`text-sm font-semibold capitalize ${r.error ? 'text-red-700' : 'text-green-700'}`}>
                    {r.platform} — {r.error ? 'Failed' : scheduleMode === 'scheduled' ? 'Scheduled' : 'Published'}
                  </p>
                  {r.postId && <p className="text-xs text-green-600 mt-0.5">Post ID: {r.postId}</p>}
                  {r.error && <p className="text-xs text-red-600 mt-0.5">{r.error}</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {globalError && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200">
            <AlertCircle size={16} className="text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{globalError}</p>
          </div>
        )}

        {/* Platform Selection */}
        <div>
          <label className="label-base mb-2">Select Platforms</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => togglePlatform('facebook')}
              className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all duration-150 ${
                platforms.facebook
                  ? 'border-blue-500 bg-blue-50' :'border-border hover:border-blue-300'
              }`}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${platforms.facebook ? 'bg-blue-500' : 'bg-muted'}`}>
                <FacebookIcon size={18} className={platforms.facebook ? 'text-white' : 'text-muted-foreground'} />
              </div>
              <div className="text-left">
                <p className={`text-sm font-semibold ${platforms.facebook ? 'text-blue-700' : 'text-foreground'}`}>Facebook</p>
                <p className="text-xs text-muted-foreground">Page post</p>
              </div>
            </button>
            <button
              onClick={() => togglePlatform('instagram')}
              className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all duration-150 ${
                platforms.instagram
                  ? 'border-pink-500 bg-pink-50' :'border-border hover:border-pink-300'
              }`}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${platforms.instagram ? 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400' : 'bg-muted'}`}>
                <InstagramIcon size={18} className={platforms.instagram ? 'text-white' : 'text-muted-foreground'} />
              </div>
              <div className="text-left">
                <p className={`text-sm font-semibold ${platforms.instagram ? 'text-pink-700' : 'text-foreground'}`}>Instagram</p>
                <p className="text-xs text-muted-foreground">Business account</p>
              </div>
            </button>
          </div>
        </div>

        {/* Caption */}
        <div>
          <label className="label-base">Caption</label>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Write a compelling caption for your post..."
            rows={3}
            className="input-base resize-none mt-1"
          />
        </div>

        {/* Meta API Credentials */}
        <div className="border border-border rounded-xl overflow-hidden">
          <button
            onClick={() => setShowCreds((v) => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center">
                <Tag size={12} className="text-primary" />
              </div>
              <span className="text-sm font-semibold text-foreground">Meta API Credentials</span>
              {creds.accessToken && creds.pageId && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Configured</span>
              )}
            </div>
            {showCreds ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
          </button>
          {showCreds && (
            <div className="p-4 pt-0 space-y-3 border-t border-border">
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-xs text-amber-700">
                  <strong>Your credentials stay in your browser.</strong> They are sent directly to the Meta Graph API and never stored on our servers. You need a Meta App with <code className="bg-amber-100 px-1 rounded">pages_manage_posts</code> and <code className="bg-amber-100 px-1 rounded">instagram_basic</code> permissions.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-base text-xs">App ID</label>
                  <input
                    type="text"
                    value={creds.appId}
                    onChange={(e) => setCreds((p) => ({ ...p, appId: e.target.value }))}
                    placeholder="Your Meta App ID"
                    className="input-base mt-1 text-sm"
                  />
                </div>
                <div>
                  <label className="label-base text-xs">App Secret</label>
                  <input
                    type="password"
                    value={creds.appSecret}
                    onChange={(e) => setCreds((p) => ({ ...p, appSecret: e.target.value }))}
                    placeholder="Your App Secret"
                    className="input-base mt-1 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="label-base text-xs">Page Access Token <span className="text-red-500">*</span></label>
                <input
                  type="password"
                  value={creds.accessToken}
                  onChange={(e) => setCreds((p) => ({ ...p, accessToken: e.target.value }))}
                  placeholder="Long-lived Page Access Token"
                  className="input-base mt-1 text-sm"
                />
              </div>
              <div>
                <label className="label-base text-xs">Facebook Page ID <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={creds.pageId}
                  onChange={(e) => setCreds((p) => ({ ...p, pageId: e.target.value }))}
                  placeholder="e.g. 123456789012345"
                  className="input-base mt-1 text-sm"
                />
              </div>
              {platforms.instagram && (
                <div>
                  <label className="label-base text-xs">Instagram Business Account ID <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={creds.igAccountId}
                    onChange={(e) => setCreds((p) => ({ ...p, igAccountId: e.target.value }))}
                    placeholder="e.g. 17841400000000000"
                    className="input-base mt-1 text-sm"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Audience Targeting */}
        <div className="border border-border rounded-xl overflow-hidden">
          <button
            onClick={() => setShowAudience((v) => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center">
                <Target size={12} className="text-primary" />
              </div>
              <span className="text-sm font-semibold text-foreground">Audience Targeting</span>
              <span className="text-[10px] font-medium text-muted-foreground">(optional)</span>
            </div>
            {showAudience ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
          </button>
          {showAudience && (
            <div className="p-4 pt-0 space-y-4 border-t border-border">
              {/* Age Range */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Users size={13} className="text-muted-foreground" />
                  <label className="label-base text-xs">Age Range</label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Min Age</label>
                    <input
                      type="number"
                      min={13}
                      max={65}
                      value={audience.ageMin}
                      onChange={(e) => setAudience((p) => ({ ...p, ageMin: Number(e.target.value) }))}
                      className="input-base mt-1 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Max Age</label>
                    <input
                      type="number"
                      min={13}
                      max={65}
                      value={audience.ageMax}
                      onChange={(e) => setAudience((p) => ({ ...p, ageMax: Number(e.target.value) }))}
                      className="input-base mt-1 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Locations */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <MapPin size={13} className="text-muted-foreground" />
                  <label className="label-base text-xs">Locations</label>
                </div>
                <input
                  type="text"
                  value={audience.locations}
                  onChange={(e) => setAudience((p) => ({ ...p, locations: e.target.value }))}
                  placeholder="e.g. New York, Los Angeles, Chicago"
                  className="input-base text-sm"
                />
                <p className="helper-text mt-1">Comma-separated city or country names</p>
              </div>

              {/* Interests */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Tag size={13} className="text-muted-foreground" />
                  <label className="label-base text-xs">Interests</label>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {INTEREST_SUGGESTIONS.map((interest) => (
                    <button
                      key={interest}
                      onClick={() => toggleInterest(interest)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-all duration-150 ${
                        selectedInterests.includes(interest)
                          ? 'border-primary bg-primary/10 text-primary font-semibold' :'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
                      }`}
                    >
                      {interest}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={audience.interests}
                  onChange={(e) => setAudience((p) => ({ ...p, interests: e.target.value }))}
                  placeholder="Or type custom interests, comma-separated"
                  className="input-base text-sm"
                />
              </div>
            </div>
          )}
        </div>

        {/* Scheduling */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={14} className="text-muted-foreground" />
            <label className="label-base">Scheduling</label>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <button
              onClick={() => setScheduleMode('now')}
              className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all duration-150 ${
                scheduleMode === 'now' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
              }`}
            >
              <Send size={15} className={scheduleMode === 'now' ? 'text-primary' : 'text-muted-foreground'} />
              <div className="text-left">
                <p className={`text-sm font-semibold ${scheduleMode === 'now' ? 'text-primary' : 'text-foreground'}`}>Post Now</p>
                <p className="text-xs text-muted-foreground">Publish immediately</p>
              </div>
            </button>
            <button
              onClick={() => setScheduleMode('scheduled')}
              className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all duration-150 ${
                scheduleMode === 'scheduled' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
              }`}
            >
              <Clock size={15} className={scheduleMode === 'scheduled' ? 'text-primary' : 'text-muted-foreground'} />
              <div className="text-left">
                <p className={`text-sm font-semibold ${scheduleMode === 'scheduled' ? 'text-primary' : 'text-foreground'}`}>Schedule</p>
                <p className="text-xs text-muted-foreground">Pick date & time</p>
              </div>
            </button>
          </div>
          {scheduleMode === 'scheduled' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-base text-xs">Date</label>
                <input
                  type="date"
                  min={minDateStr}
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  className="input-base mt-1 text-sm"
                />
              </div>
              <div>
                <label className="label-base text-xs">Time</label>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="input-base mt-1 text-sm"
                />
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 pt-2 border-t border-border">
          <button
            onClick={handleClose}
            disabled={isPublishing}
            className="btn-secondary flex-1 justify-center"
          >
            {results ? 'Close' : 'Cancel'}
          </button>
          {!results && (
            <button
              onClick={handlePublish}
              disabled={!canPublish() || isPublishing}
              className="btn-primary flex-1 justify-center"
            >
              {isPublishing ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Send size={15} />
                  {scheduleMode === 'scheduled' ? 'Schedule Post' : 'Publish Now'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
